import React, { ReactElement, useCallback, useEffect, useState } from 'react'

import Button from '@instabox/internal-web-ui/components/Button'
import notify from '@instabox/internal-web-ui/utils/notify'
import { RolloutCandidate } from '@instabox/lockers-update-common/types'

import fetch from '../../fetch'
import useCurrentRollout from '../../hooks/useCurrentRollout'

import RolloutDeviceTable from './RolloutDeviceTable'
import RolloutPackagesTable, { PackageVersion } from './RolloutPackagesTable'

function RolloutElement(): ReactElement {
  const { currentRollout, loading: loadingCurrentRollout, reload } = useCurrentRollout()

  const [loading, setLoading] = useState<boolean>(false)

  const [packages, setPackages] = useState<PackageVersion[]>([])
  const [targetDevices, setTargetDevices] = useState<string[]>([])

  const handleSelectedDevicesChanged = useCallback((deviceIds) => {
    setTargetDevices(deviceIds)
  }, [])

  const handleRolloutPackagesChanged = useCallback((ps) => {
    setPackages(ps)
  }, [])

  useEffect(() => {
    setTargetDevices(currentRollout?.targetDevices || [])
  }, [currentRollout])

  async function submitRollout(e: React.SyntheticEvent): Promise<void> {
    e.preventDefault()

    if (packages.length === 0 || packages.some((p) => p.version === '')) {
      return
    }

    setLoading(true)

    try {
      const rolloutCandidate: RolloutCandidate = {
        targetDevices,
        packages,
      }

      const response = await fetch('/api/rollout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rolloutCandidate),
      })

      if (response.ok === false) {
        throw new Error(`Unable to submit rollout: ${response.status} ${response.statusText}`)
      }

      notify({
        type: 'success',
        message: 'Rollout created!',
        theme: 'colored',
      })
    } catch (e) {
      notify({
        type: 'error',
        message: 'Failed to create rollout!',
        theme: 'colored',
      })
    } finally {
      setLoading(false)
      reload()
    }
  }

  async function abortRollout(): Promise<void> {
    if (currentRollout === undefined) {
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`/api/rollout/${currentRollout.rolloutId}/abort`, {
        method: 'PUT',
      })

      if (response.ok === false) {
        throw new Error(`Unable to abort rollout: ${response.status} ${response.statusText}`)
      }

      notify({
        type: 'success',
        message: 'Rollout aborted!',
        theme: 'colored',
      })
    } catch (e) {
      notify({
        type: 'error',
        message: 'Failed to abort rollout!',
        theme: 'colored',
      })
    } finally {
      setLoading(false)
      reload()
    }
  }

  return (
    <form onSubmit={submitRollout}>
      <div style={{ float: 'left' }}>
        <Button
          disabled={currentRollout === undefined || currentRollout.apply === false || loading}
          data-testid="rolloutAbortButton"
          className="me-3"
          size="lg"
          type="button"
          onClick={abortRollout}
          isLoading={loading}
        >
          Abort
        </Button>
        <Button
          data-testid="rolloutSubmitButton"
          size="lg"
          type="submit"
          disabled={packages.some((p) => p.version === '') || loading}
          isLoading={loading}
        >
          Start
        </Button>
      </div>

      <RolloutPackagesTable
        onPackagesChanged={handleRolloutPackagesChanged}
        currentRollout={currentRollout}
        loading={loadingCurrentRollout}
      />

      <RolloutDeviceTable
        onSelectedDevicesChanged={handleSelectedDevicesChanged}
        loading={loadingCurrentRollout}
        targetDevices={targetDevices}
      />
    </form>
  )
}

export default RolloutElement
